import { useState } from 'react'

import { useStepUp } from '../api/auth'
import { ApiError } from '../api/client'

/** Envuelve una acción sensible (alta de credencial de exchange, borrarla,
 *  más adelante pasar un bot a `live`) con la re-autenticación que exige el
 *  backend (docs/plan_bot_trading.md §3.4.1).
 *
 *  Intenta la acción directo. Si el backend responde 403 —la sesión no tiene
 *  un step-up reciente—, guarda la acción y abre el diálogo de contraseña en
 *  vez de mostrarlo como error; al confirmar, hace `/auth/step-up` y
 *  reintenta la acción original una sola vez.
 *
 *  `run` devuelve si la acción ya se ejecutó (`true`) o quedó esperando la
 *  contraseña (`false`) — el caller lo necesita para decidir si mostrar un
 *  toast de éxito: la sesión puede tener un step-up reciente y no pedir nada
 *  (caso común, la acción ya pasó) o puede no tenerlo y quedar pendiente
 *  (nada pasó todavía). Antes de esto no había forma de distinguir los dos
 *  casos desde afuera del hook.
 */
export function useWithStepUp() {
  const stepUp = useStepUp()
  const [pending, setPending] = useState<(() => Promise<void>) | null>(null)
  const [retrying, setRetrying] = useState(false)
  // Separado de `stepUp.error` (que sólo cubre el POST a /auth/step-up): la
  // acción original puede volver a fallar en el reintento por un motivo de
  // negocio (label duplicado, key inválida) — antes ese error no lo
  // capturaba nadie, era una unhandled promise rejection y el diálogo se
  // cerraba solo (por `setPending(null)`) sin mostrar nada.
  const [retryError, setRetryError] = useState<Error | null>(null)

  async function run(action: () => Promise<void>): Promise<boolean> {
    try {
      await action()
      return true
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setRetryError(null)
        setPending(() => action)
        return false
      }
      throw error
    }
  }

  async function confirm(password: string): Promise<void> {
    await stepUp.mutateAsync(password)
    const action = pending
    if (!action) {
      setPending(null)
      return
    }
    // `pending` (y con él `promptOpen`) se limpia recién cuando el
    // reintento termina, no antes — así el diálogo se queda abierto
    // mientras la acción original corre de nuevo, en vez de desmontarse
    // antes de saber si salió bien.
    setRetrying(true)
    setRetryError(null)
    try {
      await action()
    } catch (error) {
      setRetryError(error instanceof Error ? error : new Error('No se pudo completar la acción'))
      throw error
    } finally {
      setRetrying(false)
      setPending(null)
    }
  }

  return {
    run,
    promptOpen: pending !== null,
    cancel: () => {
      setPending(null)
      setRetryError(null)
    },
    confirm,
    isConfirming: stepUp.isPending || retrying,
    confirmError: stepUp.error ?? retryError,
  }
}
